import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getPerformanceBadge } from './utils';

/**
 * MetricsHeader Component
 * Displays performance dashboard with key metrics
 * Fully isolated - no dependencies on existing components
 */
const MetricsHeader = ({ metrics }) => {
    const theme = useTheme();

    const {
        avgLatency = 0,
        avgSlippage = 0,
        totalCount = 0,
        activeCount = 0,
        bestLatency = 0,
        worstLatency = 0
    } = metrics || {};

    const latencyBadge = getPerformanceBadge(avgLatency, 'latency');
    const slippageBadge = getPerformanceBadge(avgSlippage, 'slippage');
    const bestBadge = getPerformanceBadge(bestLatency, 'latency');
    const worstBadge = getPerformanceBadge(worstLatency, 'latency');

    // Compact stat card component
    const StatCard = ({ label, value, unit, badge, subtitle }) => (
        <Box
            sx={{
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                minHeight: 70
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    color: 'rgba(140, 200, 240, 1)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                    mb: 0.5
                }}
            >
                {label}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontFamily: 'monospace',
                        fontSize: '1.5rem'
                    }}
                >
                    {value}
                </Typography>
                {unit && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(140, 200, 240, 1)',
                            fontWeight: 500,
                            fontSize: '0.875rem'
                        }}
                    >
                        {unit}
                    </Typography>
                )}
            </Box>

            {badge && (
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                        gap: 0.75,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: badge.color + '15',
                        border: `1px solid ${badge.color}40`
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: badge.color
                        }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            color: badge.color,
                            fontWeight: 700,
                            fontSize: '0.75rem'
                        }}
                    >
                        {badge.label}
                    </Typography>
                </Box>
            )}

            {subtitle && (
                <Typography
                    variant="caption"
                    sx={{
                        mt: 0.5,
                        color: 'rgba(140, 200, 240, 1)',
                        fontSize: '0.75rem'
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                mb: 2,
                px: { xs: 1, sm: 2 },
                pt: 1.5
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(6, 1fr)'
                    },
                    gap: 1.5
                }}
            >
                <StatCard
                    label="Today Trades"
                    value={totalCount}
                    unit=""
                    subtitle="Total executions"
                />

                <StatCard
                    label="Avg Latency"
                    value={avgLatency}
                    unit="ms"
                    badge={latencyBadge}
                />

                <StatCard
                    label="Avg Slippage"
                    value={avgSlippage.toFixed(2)}
                    unit="pips"
                    badge={slippageBadge}
                />

                <StatCard
                    label="Best Execution"
                    value={bestLatency}
                    unit="ms"
                    badge={bestBadge}
                />

                <StatCard
                    label="Worst Execution"
                    value={worstLatency}
                    unit="ms"
                    badge={worstBadge}
                />

                <StatCard
                    label="Active Now"
                    value={activeCount}
                    unit=""
                    subtitle="Last 5 seconds"
                />
            </Box>
        </Box>
    );
};

export default MetricsHeader;
